import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Modal,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { getFirestore, collection, getDocs, doc, updateDoc, query, where, getDoc, writeBatch } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Takkevariheader from './Takkevariheader';
import moment from 'moment';
import WhatsAppIcon from '@mui/icons-material/WhatsApp'; // Import the WhatsApp icon

const db = getFirestore();

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', md: 400 },
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

function Intresttaken() {
    const [interestData, setInterestData] = useState([]);
    const [customers, setCustomers] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openModal, setOpenModal] = useState(false);
    const [selectedInterest, setSelectedInterest] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState({
        amount: '',
        paidDate: new Date().toISOString().slice(0, 10),
        note: '',
        isLoanClosed: false,
    });
    const [principalAmount, setPrincipalAmount] = useState(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);

    // Moved fetchData outside useEffect and wrapped in useCallback
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch customers
            const customersCollection = collection(db, 'takkevaricusts');
            const customerSnapshot = await getDocs(customersCollection);
            const customersMap = {};
            customerSnapshot.docs.forEach(doc => {
                const data = doc.data();
                customersMap[doc.id] = {
                    name: data.name,
                    mobile: data.mobile,
                };
            });
            setCustomers(customersMap);

            // Fetch pending interest entries
            const interestQuery = query(collection(db, 'takkevariintrests'), where("status", "==", "pending"));
            const interestSnapshot = await getDocs(interestQuery);
            const interestDocs = interestSnapshot.docs;

            // Collect unique loan IDs
            const uniqueLoanIds = [...new Set(interestDocs.map(doc => doc.data().takkevariloansId))];

            // Fetch all related loan documents
            const loanPromises = uniqueLoanIds.map(id => getDoc(doc(db, 'takkevariloans', id)));
            const loanSnapshots = await Promise.all(loanPromises);
            const loansMap = {};
            loanSnapshots.forEach(loanDoc => {
                if (loanDoc.exists()) {
                    loansMap[loanDoc.id] = loanDoc.data().amount;
                }
            });

            // Combine all data with principal amount
            const dataList = interestDocs.map(interestDoc => {
                const data = interestDoc.data();
                const principal = loansMap[data.takkevariloansId] ?? 0;
                const customerInfo = customersMap[data.customerId] || {};
                return {
                    id: interestDoc.id,
                    ...data,
                    customerName: customerInfo.name || 'N/A',
                    mobileNo: customerInfo.mobile || 'N/A',
                    payBy: data.payBy.toDate(),
                    principalAmount: principal,
                };
            });

            // Sort by payBy date in ascending order
            dataList.sort((a, b) => a.payBy.getTime() - b.payBy.getTime());

            setInterestData(dataList);
        } catch (error) {
            console.error("Error fetching data: ", error);
            toast.error("Failed to fetch data.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Call fetchData inside useEffect
    useEffect(() => {
        fetchData();
    }, [fetchData]); // Add fetchData to the dependency array

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlePayClick = (interest) => {
        setSelectedInterest(interest);
        setPaymentDetails({
            amount: interest.interestAmount?.toFixed(2) ?? '0.00',
            paidDate: new Date().toISOString().slice(0, 10),
            note: '',
            isLoanClosed: false,
        });
        setPrincipalAmount(interest.principalAmount);
        setOpenModal(true);
    };

    const handleModalClose = () => {
        setOpenModal(false);
        setSelectedInterest(null);
        setPrincipalAmount(null);
    };

    const handlePaymentDetailsChange = (e) => {
        const { name, value, checked, type } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setPaymentDetails(prev => ({
            ...prev,
            [name]: newValue,
        }));

        if (name === 'isLoanClosed') {
            if (checked) {
                // When closing the loan, set the amount to principal + interest
                setPaymentDetails(prev => ({
                    ...prev,
                    amount: (principalAmount + selectedInterest.interestAmount).toFixed(2),
                }));
            } else {
                // Revert to just the interest amount
                setPaymentDetails(prev => ({
                    ...prev,
                    amount: selectedInterest.interestAmount.toFixed(2),
                }));
            }
        }
    };

    const handleConfirmPayment = async () => {
        if (!paymentDetails.amount) {
            toast.error("Amount is required.");
            return;
        }

        setIsProcessingPayment(true);
        const batch = writeBatch(db);

        try {
            const interestDocRef = doc(db, 'takkevariintrests', selectedInterest.id);
            batch.update(interestDocRef, {
                status: 'paid',
                paidDate: new Date(paymentDetails.paidDate),
                note: paymentDetails.note,
            });

            if (paymentDetails.isLoanClosed) {
                const loanDocRef = doc(db, 'takkevariloans', selectedInterest.takkevariloansId);
                batch.update(loanDocRef, {
                    status: 'closed',
                    closedDate: new Date(),
                });

                // Remove all other pending interest entries for this loan
                const q = query(collection(db, 'takkevariintrests'), where("takkevariloansId", "==", selectedInterest.takkevariloansId), where("status", "==", "pending"));
                const querySnapshot = await getDocs(q);
                querySnapshot.docs.forEach(doc => {
                    batch.delete(doc.ref);
                });
            } else {
                // If not closed, create the next month's entry
                const nextPayByDate = moment(selectedInterest.payBy).add(1, 'month').toDate();
                const newInterestDocRef = doc(collection(db, 'takkevariintrests'));
                batch.set(newInterestDocRef, {
                    customerId: selectedInterest.customerId,
                    takkevariloansId: selectedInterest.takkevariloansId,
                    interestAmount: selectedInterest.interestAmount,
                    paidDate: null,
                    payBy: nextPayByDate,
                    status: 'pending',
                    note: '',
                    createdAt: new Date(),
                });
            }

            await batch.commit();

            toast.success("Payment recorded successfully!");
            handleModalClose();

            // Refresh data after successful payment
            await fetchData();

        } catch (error) {
            console.error("Error processing payment: ", error);
            toast.error("Failed to process payment.");
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handleSendWhatsApp = (interest) => {
        const mobileNumber = interest.mobileNo.startsWith('+') ? interest.mobileNo.substring(1) : interest.mobileNo;
        const formattedMobileNumber = `91${mobileNumber}`; // Assuming India's country code +91
        const formattedDate = moment(interest.payBy).format('DD-MM-YYYY');
        const message = `Hello ${interest.customerName}, this is a friendly reminder that your interest payment of ₹${interest.interestAmount.toFixed(2)} was due on ${formattedDate}. Please make the payment as soon as possible. Thank you!`;
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${formattedMobileNumber}?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
    };

    const filteredData = interestData.filter(item =>
        item.customerName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Takkevariheader />
            <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', color: '#1976d2', mb: 3 }}>
                    Pending Interest Payments
                </Typography>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TextField
                        label="Search by Customer Name"
                        variant="outlined"
                        size='small'
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    />
                </Box>
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>Sr No</TableCell>
                                <TableCell>Actions</TableCell>
                                <TableCell>खातेदाराचे नाव      </TableCell>
                                <TableCell>मोबाईल</TableCell>
                                <TableCell>घेतलेली रक्कम (₹)</TableCell>
                                <TableCell>व्याज (₹)</TableCell>
                                <TableCell>व्याज तारीख</TableCell>
                                <TableCell>स्टेटस</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((row, index) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ display: 'flex', gap: 1 }}>
                                            {row.status === 'pending' && (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handlePayClick(row)}
                                                    >
                                                        Pay
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        startIcon={<WhatsAppIcon />}
                                                        onClick={() => handleSendWhatsApp(row)}
                                                    >
                                                        Alert
                                                    </Button>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>{row.customerName}</TableCell>
                                        <TableCell>{row.mobileNo}</TableCell>
                                        <TableCell>{row.principalAmount?.toFixed(2) ?? '0.00'}</TableCell>
                                        <TableCell>{row.interestAmount?.toFixed(2) ?? '0.00'}</TableCell>
                                        <TableCell>{moment(row.payBy).format('DD-MM-YYYY')}</TableCell>
                                        <TableCell>{row.status}</TableCell>

                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No pending interest payments found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Modal open={openModal} onClose={handleModalClose}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2" mb={2}>
                         {selectedInterest?.customerName} साठी पेमेंट नोंदवा
                    </Typography>
                    <Grid container className="row">
                        <Grid className="col-sm-12 mb-4" item xs={12}>
                            <TextField
                                fullWidth
                                size='small'
                                label="रक्कम (₹)"
                                name="amount"
                                type="number"
                                value={paymentDetails.amount}
                                onChange={handlePaymentDetailsChange}
                                disabled={paymentDetails.isLoanClosed}
                            />
                        </Grid>
                        <Grid className="col-sm-12 mb-4" item xs={12}>
                            <TextField
                                fullWidth
                                size='small'
                                label="पेड दिनांक"
                                type="date"
                                name="paidDate"
                                value={paymentDetails.paidDate}
                                onChange={handlePaymentDetailsChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid className="col-sm-12 mb-4" item xs={12}>
                            <TextField
                                fullWidth
                                size='small'
                                label="नोंद (ऐच्छिक)"
                                name="note"
                                value={paymentDetails.note}
                                onChange={handlePaymentDetailsChange}
                            />
                        </Grid>
                        <Grid className="col-sm-12 mb-4" item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={paymentDetails.isLoanClosed}
                                        onChange={handlePaymentDetailsChange}
                                        name="isLoanClosed"
                                    />
                                }
                                label="मुद्दल आता जमा करणार आहेत का "
                            />
                        </Grid>
                        {principalAmount !== null && (
                            <Grid className="col-sm-12 mb-4" item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    मूळ रक्कम: ₹{principalAmount.toFixed(2)}
                                </Typography>
                            </Grid>
                        )}
                        <Grid className="col-sm-12 mb-4" item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={handleModalClose} variant="outlined" color="error">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirmPayment}
                                variant="contained"
                                disabled={isProcessingPayment}
                            >
                                {isProcessingPayment ? <CircularProgress size={24} /> : 'पेमेंट कन्फर्म करा'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
        </Box>
    );
}

export default Intresttaken;